import * as Sentry from '@sentry/react';

/**
 * Logs an error to Sentry with context
 * @param {Error} error - The error to log
 * @param {Object} options - Configuration options
 * @param {string} options.component - Component or module name where error occurred
 * @param {Object} options.tags - Additional tags for Sentry (e.g., { funnelId: '14', componentName: 'Landing' })
 * @param {Object} options.extra - Additional context data
 * @param {string} options.level - Error level: 'error', 'warning', 'info' (default: 'error')
 * @param {string} options.errorType - Type of error (e.g., 'chunk_loading', 'file_not_found', 'unexpected')
 */
export const logErrorToSentry = (error, options = {}) => {
  const {
    component,
    tags = {},
    extra = {},
    level = 'error',
    errorType,
  } = options;

  // Build tags object
  const sentryTags = {
    ...tags,
    ...(component && { component }),
    ...(errorType && { errorType }),
  };

  // Build extra data
  const sentryExtra = {
    ...extra,
    ...(error && {
      errorMessage: error.message,
      errorName: error.name,
      ...(error.stack && { stack: error.stack }),
    }),
  };

  Sentry.captureException(error, {
    tags: sentryTags,
    extra: sentryExtra,
    level,
  });
};

/**
 * Logs a message to Sentry
 * @param {string} message - The message to log
 * @param {Object} options - Configuration options
 * @param {string} options.level - Message level: 'error', 'warning', 'info' (default: 'info')
 * @param {Object} options.tags - Additional tags for Sentry
 * @param {Object} options.extra - Additional context data
 */
export const logMessageToSentry = (message, options = {}) => {
  const {
    level = 'info',
    tags = {},
    extra = {},
  } = options;

  Sentry.captureMessage(message, {
    tags,
    extra,
    level,
  });
};

